import { CompanyDouble } from '@app/components/Home/Blocks/MainFilter/const';
import { useOnBlur } from '@hooks/useOnBlur';
import { IconChevronDown } from '@koleso-icons';
import IconStar from '@sharedUi/ui-kit/FormField/ui/FormField/icons/star.svg';
import { ScrollBar } from '@ui-kit/ScrollBar';
import { Error } from '@ui-kit/Select/Error';
import classNames from 'classnames';
import IconArrow from 'public/next/ui-kit/select/arrow.svg';
import IconRemove from 'public/next/ui-kit/select/close.svg';
import { useCallback, useEffect, useRef, useState } from 'react';
import { PropsSelect } from './PropsSelect';

import styles from './Select.module.scss';

export const Select = ({
  valueClassName,
  className,
  isLoading,
  isRequired,
  label,
  placeholder,
  activeValue,
  dataList,
  disabled,
  startValue,
  readOnly,
  handlerValue,
  error,
  errorsMessage,
  fullWidth,
  isNative,
  dataTestId,
  onClick,
}: PropsSelect): JSX.Element => {
  const [show, setShow] = useState<boolean>(false);
  const [list, setList] = useState<string[]>(dataList);
  const [value, setValue] = useState<string>('');
  const [BlockListRef, setBlockListRef] = useState<HTMLDivElement>(null);
  const ContainerRef = useRef<HTMLDivElement>(null);
  const InputRef = useRef<HTMLInputElement>(null);
  // выбор значение из списка
  const handlerClick = value => {
    value !== 'нет результата' && handlerValue(value);
    setShow(false);
  };

  const handlerChangeNativeSelect = event => {
    const value = event.target.value;
    handlerValue(value);
    setShow(false);
  };

  // очистка поля
  const handlerClear = useCallback(() => {
    setValue('');
    setList(dataList);
    InputRef.current?.focus();
  }, []);

  // обработка ввода
  const HandlerEnter = e => {
    setValue(e.target.value);
    if (e.target?.value.length > 0) {
      const newList = [...dataList].filter(x => x.toString().toLowerCase().includes(e.target.value.toString().toLowerCase()));
      if (newList.length === 1) {
        handlerClick(newList[0]);
      }
      newList.length ? setList(newList) : setList(['нет результата']);
    } else {
      setList(dataList);
    }
  };

  const HandlerOpen = () => {
    !disabled && setShow(true);
    onClick && onClick();
  };

  // закрытие при нажатии вне окна
  useOnBlur(ContainerRef, setShow);

  useEffect(() => {
    if (show) {
      InputRef.current.focus();
    }
    // скролл к стартовому эдементу
    if (startValue && show && BlockListRef) {
      const element = Array.from(BlockListRef.childNodes)[startValue] as any;
      BlockListRef.scrollTop = element?.offsetTop;
    }
    setValue('');
    setList(dataList);
  }, [show, dataList]);

  return (
    <div
      className={classNames(
        styles.Wrapper,
        {
          [styles.w100]: fullWidth,
          [styles.isRequired]: isRequired,
        },
        className,
      )}
    >
      {label && (
        <label>
          {label}
          {isRequired && <IconStar width={12} height={12} />}
        </label>
      )}
      {isNative ? (
        <Select.NativeSelect
          list={list}
          activeValue={activeValue}
          dataTestId={dataTestId}
          handlerChangeNativeSelect={handlerChangeNativeSelect}
        />
      ) : (
        <Select.CustomSelect
          show={show}
          value={value}
          list={list}
          readOnly={readOnly}
          placeholder={placeholder}
          InputRef={InputRef}
          dataTestId={dataTestId}
          activeValue={activeValue}
          valueClassName={valueClassName}
          ContainerRef={ContainerRef}
          disabled={disabled}
          error={error}
          errorsMessage={errorsMessage}
          isLoading={isLoading}
          setBlockListRef={setBlockListRef}
          HandlerOpen={HandlerOpen}
          HandlerEnter={HandlerEnter}
          handlerClear={handlerClear}
          handlerClick={handlerClick}
        />
      )}
    </div>
  );
};

Select.CustomSelect = function CustomSelect({
  show,
  value,
  list,
  readOnly,
  placeholder,
  InputRef,
  isLoading,
  ContainerRef,
  dataTestId,
  disabled,
  error,
  errorsMessage,
  activeValue,
  valueClassName,
  setBlockListRef,
  HandlerOpen,
  HandlerEnter,
  handlerClear,
  handlerClick,
}) {
  return (
    <>
      <div
        ref={ContainerRef}
        className={classNames(
          styles.Container,
          show && styles.Active,
          !list.length && styles.ValueNull,
          isLoading && styles.Loading,
          disabled && styles.Disabled,
          error && styles.Error,
        )}
      >
        <div className={styles.Value} onClick={HandlerOpen} data-testid={dataTestId}>
          {show ? (
            <input ref={InputRef} value={value} readOnly={readOnly} onChange={HandlerEnter} />
          ) : (
            <div className={classNames(styles.Text, valueClassName)} onMouseDown={HandlerOpen}>
              {activeValue || <span className={styles.Placeholder}>{placeholder}</span>}
            </div>
          )}
          {value.length > 0 && show ? (
            <IconRemove onClick={handlerClear} width={10} hieght={20} className={styles.BtnRemove} />
          ) : (
            <IconArrow width={10} hieght={5} className={styles.Arrow} />
          )}
        </div>
        <section>
          <div className={styles.BoxList}>
            <ScrollBar type={'vertical'} maxH={show ? 205 : 0} getContainerRef={ref => setBlockListRef(ref)}>
              {list.map(item => (
                <li
                  value={item}
                  className={classNames(activeValue.toString() === item.toString() && styles.Active, {
                    [styles.Active]:
                      (activeValue.toString() === CompanyDouble.New && item.toString() === CompanyDouble.Old) ||
                      (activeValue.toString() === CompanyDouble.Old && item.toString() === CompanyDouble.New),
                  })}
                  onClick={() => handlerClick(item)}
                  key={item}
                >
                  <span>{item}</span>
                </li>
              ))}
            </ScrollBar>
          </div>
        </section>
      </div>
      <Error error={errorsMessage} />
    </>
  );
};

Select.NativeSelect = function NativeSelect({ list, activeValue, dataTestId, handlerChangeNativeSelect }) {
  return (
    <div className={styles.ContainerNativeSelect}>
      <select value={activeValue} onChange={handlerChangeNativeSelect} data-testid={dataTestId}>
        {list.map(value => (
          <option key={value} value={value}>
            {value}
          </option>
        ))}
      </select>
      <IconChevronDown />
    </div>
  );
};
